import {
    Directive,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    Optional,
} from '@angular/core';
import {
    AbstractControl,
    NgControl,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';

@Directive({
    selector: '[appRegexInput]',
    standalone: true,
})
export class RegexInputDirective implements OnInit {
    @Input('appRegexInput') regexPattern: string | RegExp = '';
    private regex!: RegExp;

    constructor(
        private el: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
        @Optional() private ngControl: NgControl,
    ) {}

    ngOnInit() {
        this.initializeRegex();
        this.addValidator();
    }

    @HostListener('beforeinput', ['$event'])
    onBeforeInput(event: InputEvent) {
        if (event.inputType === 'insertText' && event.data) {
            if (!this.regex.test(event.data)) {
                event.preventDefault();
            }
        }
    }

    @HostListener('input', ['$event'])
    onInput(event: Event) {
        const targetElement = this.getTargetElement(event);
        if (!targetElement) {
            return;
        }

        const currentValue = targetElement.value;
        const sanitizedValue = this.sanitizeData(currentValue);

        if (sanitizedValue !== currentValue) {
            this.updateValue(sanitizedValue);
            const cursorPosition = targetElement.selectionStart;
            if (cursorPosition !== null) {
                targetElement.setSelectionRange(cursorPosition, cursorPosition);
            }
        }
    }

    /**
     * Retrieves the target input or textarea element from the event.
     *
     * @param event The input event.
     * @returns The target HTMLInputElement or HTMLTextAreaElement, or null if not found.
     */
    private getTargetElement(
        event: Event,
    ): HTMLInputElement | HTMLTextAreaElement | null {
        const target = event.target;

        if (
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement
        ) {
            return target;
        }

        return null;
    }

    private initializeRegex() {
        this.regex =
            typeof this.regexPattern === 'string'
                ? new RegExp(this.regexPattern)
                : this.regexPattern;
    }

    private addValidator() {
        if (this.ngControl && this.ngControl.control) {
            const control = this.ngControl.control;
            const validatorFn = this.createValidator();
            control.addValidators(validatorFn);
            control.updateValueAndValidity();
        }
    }

    private createValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (value === null || value === '') {
                return null;
            }
            const valid = this.regex.test(value);
            return valid ? null : { pattern: { value: value } };
        };
    }

    private sanitizeData(data: string): string {
        return data
            .split('')
            .filter((char) => this.regex.test(char))
            .join('');
    }

    private updateValue(value: string) {
        this.el.nativeElement.value = value;
        if (this.ngControl?.control) {
            this.ngControl.control.setValue(value, { emitEvent: false });
            this.ngControl.control.updateValueAndValidity();
        }
    }
}
